import {
	KEYWORD_DECORATION_CLASS,
	KEYWORD_STYLER_LOADING_CLASS,
	KEYWORD_STYLER_CLASS,
	KEYWORD_STYLER_HOVER_CLASS,
} from '../../../ui/utils/const';
import { isKeywordNode } from './keyword';

/**
 * set keyword loading state
 */
export const setKeywordStylerLoading = (id: string): void => {
	const styler = document.querySelector(`.${KEYWORD_STYLER_CLASS}-${id}`);
	if (styler) {
		styler.classList.add(`${KEYWORD_STYLER_LOADING_CLASS}`);
	}
};

// remove loading state from keyword styler, easier to use without passing id
export const resetKeywordStylerLoading = (): void => {
	const stylerArr = document.querySelectorAll(`.${KEYWORD_STYLER_LOADING_CLASS}`);
	for (const styler of stylerArr) {
		styler.classList.remove(`${KEYWORD_STYLER_LOADING_CLASS}`);
	}
};

/**
 * when hover keyword, show the background
 */
export const onMouseoverKeywordStyler = (event: MouseEvent): void => {
	const { target } = event;
	if (isKeywordNode(target) && target.dataset.id) {
		const id = target.dataset.id;
		const styler = document.querySelector(`.${KEYWORD_STYLER_CLASS}-${id}`);
		if (styler) {
			styler.classList.add(`${KEYWORD_STYLER_HOVER_CLASS}`);
		}
	}
};

export const onMouseoutKeywordStyler = (event: MouseEvent): void => {
	const { target } = event;
	if (isKeywordNode(target) && target.dataset.id) {
		const id = target.dataset.id;
		const styler = document.querySelector(`.${KEYWORD_STYLER_CLASS}-${id}`);
		if (styler) {
			styler.classList.remove(`${KEYWORD_STYLER_HOVER_CLASS}`);
		}
	}
};

/**
 * position the widget decoration node
 * make it the equal size as the inline keyword decoration span node/nodes
 */
export const updateKeywordStylerPosition = (styler: HTMLSpanElement): void => {
	const id = styler.dataset.id;
	if (!id) {
		return;
	}

	// there might be multiple consecutive keyword inline decorations if there are other decorations on the keyword text
	const keywordDecorationArr = document.querySelectorAll(`.${KEYWORD_DECORATION_CLASS}-${id}`);
	const keywordStart = keywordDecorationArr[0];
	const keywordEnd = keywordDecorationArr[keywordDecorationArr.length - 1];

	if (
		styler &&
		styler instanceof HTMLSpanElement &&
		isKeywordNode(keywordStart) &&
		isKeywordNode(keywordEnd)
	) {
		const range = document.createRange();
		range.setStartBefore(keywordStart);
		range.setEndAfter(keywordEnd);
		const rect = range.getBoundingClientRect();
		styler.style.setProperty('--ai-definitions-keyword-styler-width', `${rect.width}px`);
		styler.style.setProperty('--ai-definitions-keyword-styler-height', `${rect.height}px`);
	}
};

export function updateAllKeywordStylerPositions(): void {
	const containers = document.querySelectorAll(`.${KEYWORD_STYLER_CLASS}`);
	for (const container of containers) {
		if (container instanceof HTMLSpanElement) {
			updateKeywordStylerPosition(container);
		}
	}
}

export function setupKeywordStylerMutationObserver(dom: HTMLElement) {
	const observer = new MutationObserver((records) => {
		for (const record of records) {
			for (const node of record.addedNodes) {
				if (node instanceof HTMLSpanElement && node.classList.contains(KEYWORD_STYLER_CLASS)) {
					updateKeywordStylerPosition(node);
				}
			}
		}
	});

	observer.observe(dom, {
		childList: true,
		subtree: true,
	});

	return (): void => {
		observer.disconnect();
	};
}

/**
 * When creating this node, the corresponding inline decoration span isn't created yet,
 * so we have to use MutationObserver to position this node after it's added to DOM.
 */
export const createKeywordStyler = ({ id }: { id: string }): HTMLSpanElement => {
	// add an extra relative container to make the positioning easier
	const container = document.createElement('span');
	container.classList.add(KEYWORD_STYLER_CLASS, `${KEYWORD_STYLER_CLASS}-${id}`);
	container.dataset.id = id;
	return container;
};
